import { faArrowToTop } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@kcftech/design-system';
import { JobType } from '@kcftech/prometheustypescriptapiclient';
import { Spinner } from 'components/spinner/Spinner';
import { useSetupToolTemplateUpload } from 'custom_hooks/data_hooks/setup_tools/useSetupToolTemplateUpload';
import { uploadFile } from 'utils/file';

export type SetupToolsTemplateUploadButtonProps = Readonly<{
    locationId: string | undefined;
    jobType: JobType;
    /** Callback for when the upload has started */
    onStart: () => void;
    /**
     * Callback for when the file has been uploaded
     */
    onComplete: (errors?: string[]) => void;
}>;

export function SetupToolsTemplateUploadButton({
    locationId,
    jobType,
    onStart,
    onComplete,
}: SetupToolsTemplateUploadButtonProps) {
    const {
        isPending: isUploading,
        mutateAsync: runSetupTool,
    } = useSetupToolTemplateUpload({
        onSuccess: () => onComplete(),
        onError: (response) => {
            onComplete(Object.values(response.Errors));
        },
    });

    const onTemplateUpload = () => {
        uploadFile(
            async (uploadedFile) => {
                if (!locationId) {
                    throw new Error('Attempted to run setup tool without location id');
                }
                onStart();
                await runSetupTool({
                    type: jobType,
                    locationId,
                    template: uploadedFile,
                });
            },
            ['.xlsx']
        );
    };

    return (
        <Button
            className="mr-20"
            appearance="primary"
            spacing="medium"
            icon={
                <span className="pr-10">
                    {isUploading ? (
                        <Spinner size="1x" />
                    ) : (
                        <FontAwesomeIcon icon={faArrowToTop} />
                    )}
                </span>
            }
            disabled={locationId === undefined || isUploading}
            onClick={() => onTemplateUpload()}
        >
            Upload File
        </Button>
    );
}
